// src/components/white-screen-tool/WhiteScreenTool.tsx
import { useState, useCallback, useEffect } from 'react';
import { Download, Maximize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useParams } from 'react-router-dom';
import ScreenCard from './ScreenCard';
import ColorPicker from './ColorPicker';
import ResolutionSelector from './ResolutionSelector';
import SeoContent from '@/components/seo/SeoContent';
import FullscreenView from './FullscreenView';
import { RESOLUTIONS } from './constants';
import Head from '../layout/Head';
import { useLocalization } from '@/hooks/useLocalization';
import { useColorState } from '@/hooks/useColorState';
import { useTranslations } from '@/hooks/useTranslations';
import { useLoading } from '@/hooks/useLoading';
import { getColorNameForLanguage } from '@/config/colors';
import PageSkeleton from '@/components/ui/loading/PageSkeleton';
import TransitionWrapper from '@/components/ui/loading/TransitionWrapper';

const WhiteScreenTool = () => {
  const { currentLang } = useLocalization();
  const { content } = useTranslations();
  const { isLoading, startLoading, stopLoading } = useLoading();
  const { 'color-screen': colorPath } = useParams<{ 'color-screen': string }>();
  const { selectedColor, changeColor, updateColorFromPath } = useColorState(colorPath);
  const [customColorInput, setCustomColorInput] = useState(selectedColor);
  const [resolution, setResolution] = useState('1080p');
  const [customWidth, setCustomWidth] = useState('1920');
  const [customHeight, setCustomHeight] = useState('1080');
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Initial loading effect
  useEffect(() => {
    startLoading();
    const timer = setTimeout(() => {
      stopLoading();
    }, 1000);

    return () => {
      clearTimeout(timer);
      stopLoading();
    };
  }, [startLoading, stopLoading]);

  // Update color from URL path
  useEffect(() => {
    if (colorPath) {
      updateColorFromPath(colorPath);
    }
  }, [colorPath, updateColorFromPath]);

  // Sync custom color input with selected color
  useEffect(() => {
    setCustomColorInput(selectedColor);
  }, [selectedColor]);

  const handleCustomColorChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const color = e.target.value.toUpperCase();
    setCustomColorInput(color);
    
    if (/^#[0-9A-F]{6}$/i.test(color)) {
      changeColor(color);
    }
  }, [changeColor]);

  const handleFullscreenChange = useCallback(() => {
    const isFullscreenMode = !!document.fullscreenElement;
    setIsFullscreen(isFullscreenMode);
    
    if (isFullscreenMode) {
      document.body.classList.add('fullscreen-mode');
    } else {
      document.body.classList.remove('fullscreen-mode');
    }
  }, []);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        toast.success(content.messages.fullscreenEnter);
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error('Error toggling fullscreen:', err);
      toast.error(content.messages.fullscreenError);
    }
  }, [content.messages]);

  const downloadBackground = useCallback(async () => {
    try {
      startLoading();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Canvas context not available');
      }

      const width = resolution === 'custom' 
        ? parseInt(customWidth) 
        : RESOLUTIONS.find(r => r.label === resolution)?.width || 1920;
      
      const height = resolution === 'custom' 
        ? parseInt(customHeight) 
        : RESOLUTIONS.find(r => r.label === resolution)?.height || 1080;

      canvas.width = width;
      canvas.height = height;
      
      ctx.fillStyle = selectedColor;
      ctx.fillRect(0, 0, width, height);

      const link = document.createElement('a');
      const colorName = getColorNameForLanguage(selectedColor, currentLang).toLowerCase();
      link.download = `${colorName}-${width}x${height}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success(content.messages.downloadSuccess);
    } catch (err) {
      console.error('Error downloading background:', err);
      toast.error(content.messages.downloadError);
    } finally {
      stopLoading();
    }
  }, [selectedColor, resolution, customWidth, customHeight, currentLang, content.messages, startLoading, stopLoading]);

  // Handle keyboard shortcuts and fullscreen changes
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (document.fullscreenElement) {
        if (
          e.key === 'Escape' || // Windows users
          (e.metaKey && e.ctrlKey && e.key.toLowerCase() === 'f') // Mac users
        ) {
          document.exitFullscreen().catch(err => {
            console.error('Error exiting fullscreen:', err);
            toast.error(content.messages.fullscreenError);
          });
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [handleFullscreenChange, content.messages]);

  // Render fullscreen view when active
  if (isFullscreen) {
    return (
      <FullscreenView 
        color={selectedColor} 
        onExit={() => {
          document.exitFullscreen().catch(err => {
            console.error('Error exiting fullscreen:', err);
            toast.error(content.messages.fullscreenError);
          });
        }} 
      />
    );
  }

  // Show loading skeleton
  if (isLoading) {
    return <PageSkeleton />;
  }

  // Get color name for display
  const colorName = getColorNameForLanguage(selectedColor, currentLang);

  return (
    <TransitionWrapper isVisible={!isLoading}>
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        {/* SEO Head */}
        <Head color={selectedColor} />
        
        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-6 py-8 space-y-8">
          <div className="space-y-4">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 text-center">
              {colorName}
            </h1>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[1fr,300px] gap-8">
            {/* Left Column - Color Display */}
            <div className="space-y-8">
              <ScreenCard
                color={selectedColor}
                isFullscreen={isFullscreen}
                onToggleFullscreen={toggleFullscreen}
              />
              <ColorPicker onColorSelect={changeColor} />
            </div>

            {/* Right Column - Controls */}
            <div className="h-[300px] bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200/50 shadow-lg">
              <div className="h-full flex flex-col justify-between p-6">
                <div className="space-y-6">
                  {/* Custom Color Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {content.headings.customColor}
                    </label>
                    <Input
                      type="text"
                      value={customColorInput}
                      onChange={handleCustomColorChange}
                      placeholder="#FFFFFF"
                      className="font-mono rounded-lg border-gray-200"
                      aria-label={content.aria.customColorInput}
                    />
                  </div>

                  {/* Resolution Selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {content.headings.resolution}
                    </label>
                    <ResolutionSelector
                      resolution={resolution}
                      customWidth={customWidth}
                      customHeight={customHeight}
                      onResolutionChange={setResolution}
                      onCustomWidthChange={setCustomWidth}
                      onCustomHeightChange={setCustomHeight}
                      resolutions={RESOLUTIONS}
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Button 
                    className="w-full font-medium rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 transition-all duration-300 shadow-lg"
                    onClick={toggleFullscreen}
                    aria-label={content.aria.fullscreenButton}
                  >
                    <Maximize2 className="mr-2 h-4 w-4" />
                    {content.buttons.fullscreen}
                  </Button>
                  
                  <Button 
                    variant="outline"
                    className="w-full font-medium rounded-xl border-gray-200 hover:bg-gray-50 transition-colors shadow-sm"
                    onClick={downloadBackground}
                    aria-label={content.aria.downloadButton}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    {content.buttons.download}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* SEO Content */}
          <SeoContent />
        </div>
      </div>
    </TransitionWrapper>
  );
};

export default WhiteScreenTool;